import { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { WordDiscovery } from '../components/sections/word-discovery';
import type { SortOption, ForeignWord } from '../types/word-discovery';
import type { VoteType } from '../types/alternatives-voting';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { trackFilter, trackVote } from '../lib/analytics';
import { useExtractUsers } from '../hooks';

interface SupabaseProfile {
  id: string;
  name: string;
  avatar_url?: string | null;
}

interface SupabaseWord {
  id: string;
  word: string;
  word_arabic: string;
  category_id: string;
  category?: {
    id: string;
    name: string;
    name_english: string;
  };
  submitted_by: string;
  submitted_at: string;
  context: string;
  total_votes: number;
  alternatives_count: number;
  alternatives: SupabaseAlternative[];
  submitter?: SupabaseProfile;
}

interface SupabaseAlternative {
  id: string;
  egyptian_word?: string;
  text?: string;
  explanation: string;
  net_score?: number;
  upvotes?: number;
  downvotes?: number;
  proposed_by?: string;
  proposedBy?: string;
  proposed_at?: string;
  proposedAt?: string;
  proposer?: SupabaseProfile;
}

interface SupabaseCategory {
  id: string;
  name: string;
  name_english: string;
}

interface SupabaseVote {
  alternative_id: string;
  vote_type: string;
}

export function Discover() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [sortBy, setSortBy] = useState<SortOption>('popularity');
  const searchQuery = searchParams.get('search') || '';

  const [loading, setLoading] = useState(true);
  const [foreignWords, setForeignWords] = useState<SupabaseWord[]>([]);
  const [categories, setCategories] = useState<SupabaseCategory[]>([]);
  const [userVotes, setUserVotes] = useState<Record<string, VoteType>>({});

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('categories')
          .select('*')
          .order('name');

        if (categoriesError) throw categoriesError;

        let query = supabase.from('foreign_words').select(`
            *,
            category:categories(*),
            submitter:profiles!foreign_words_submitted_by_fkey(*),
            alternatives(
              *,
              proposer:profiles!alternatives_proposed_by_fkey(*)
            )
          `);

        if (selectedCategoryId) {
          query = query.eq('category_id', selectedCategoryId);
        }

        if (sortBy === 'popularity') {
          query = query.order('total_votes', { ascending: false });
        } else {
          query = query.order('submitted_at', { ascending: false });
        }

        const { data: wordsData, error: wordsError } = await query;

        if (wordsError) throw wordsError;

        setCategories(categoriesData || []);
        setForeignWords(wordsData || []);

        if (user && wordsData) {
          const allAlternativeIds = wordsData.flatMap((word) =>
            word.alternatives.map((alt: SupabaseAlternative) => alt.id),
          );

          if (allAlternativeIds.length > 0) {
            const { data: votesData } = await supabase
              .from('votes')
              .select('alternative_id, vote_type')
              .eq('user_id', user.id)
              .in('alternative_id', allAlternativeIds);

            const votesMap =
              votesData?.reduce(
                (acc, vote: SupabaseVote) => {
                  acc[vote.alternative_id] = vote.vote_type as VoteType;
                  return acc;
                },
                {} as Record<string, VoteType>,
              ) || {};

            setUserVotes(votesMap);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [selectedCategoryId, sortBy, user]);

  const filteredAndSortedWords = useMemo(() => {
    const transformedWords = foreignWords.map((word: SupabaseWord) => {
      const alternatives = (word.alternatives || []).map(
        (alt: SupabaseAlternative) => ({
          id: alt.id,
          text: alt.egyptian_word || alt.text || '',
          explanation: alt.explanation,
          voteCount: alt.net_score ?? 0,
          upvotes: alt.upvotes ?? 0,
          downvotes: alt.downvotes ?? 0,
          proposedBy: alt.proposed_by || alt.proposedBy || '',
          proposedAt: alt.proposed_at || alt.proposedAt || '',
        }),
      );

      const topAlternative =
        alternatives.length > 0
          ? alternatives.reduce((max, current) =>
              current.voteCount > max.voteCount ? current : max,
            )
           : {
               id: '',
               text: 'لا توجد بدائل حالياً',
               explanation: '',
               voteCount: 0,
               proposedBy: '',
               proposedAt: '',
             };

      return {
        id: word.id,
        word: word.word,
        wordArabic: word.word_arabic,
        categoryId: word.category_id,
        categoryName: word.category?.name || '',
        categoryNameEnglish: word.category?.name_english || '',
        submittedBy: word.submitted_by || '',
        submittedAt: word.submitted_at,
        context: word.context,
        totalVotes: word.total_votes || 0,
        alternativesCount: word.alternatives_count || 0,
        topAlternative,
        alternatives,
      };
    });

    if (!searchQuery) {
      return transformedWords;
    }

    const query = searchQuery.toLowerCase();
    return transformedWords.filter(
      (word) =>
        word.word.toLowerCase().includes(query) ||
        word.wordArabic.includes(query) ||
        word.context.toLowerCase().includes(query),
    );
  }, [foreignWords, searchQuery]);

  const handleFilterCategory = (categoryId: string | null) => {
    setSelectedCategoryId(categoryId);

    // Track filter usage
    if (categoryId) {
      const category = categories.find((c) => c.id === categoryId);
      if (category) {
        trackFilter('category', category.name);
      }
    }
  };

  const handleSort = (newSortBy: SortOption) => {
    setSortBy(newSortBy);
  };



  const handleVote = async (alternativeId: string, voteType: 'upvote' | 'downvote') => {
    if (!user) {
      toast.error('يرجى تسجيل الدخول للتصويت');
      return;
    }

    try {
      const currentVote = userVotes[alternativeId];
      let newVoteType: 'upvote' | 'downvote' | null = null;

      // Handle upvote/downvote/remove logic
      if (currentVote === voteType) {
        // Clicking same button again removes the vote
        newVoteType = null;
      } else {
        // Clicking different button sets new vote
        newVoteType = voteType;
      }

      if (newVoteType) {
        // Insert or update vote
        const { error } = await supabase.from('votes').upsert({
          alternative_id: alternativeId,
          user_id: user.id,
          vote_type: newVoteType,
        }, {
          onConflict: 'alternative_id,user_id'
        });

        if (error) throw error;
      } else {
        // Remove vote
        const { error } = await supabase
          .from('votes')
          .delete()
          .eq('alternative_id', alternativeId)
          .eq('user_id', user.id);

        if (error) throw error;
      }

      // Update local state
      if (newVoteType) {
        setUserVotes((prev) => ({
          ...prev,
          [alternativeId]: newVoteType,
        }));
      } else {
        // Remove vote from local state
        setUserVotes((prev) => {
          const updated = { ...prev };
          delete updated[alternativeId];
          return updated;
        });
      }

      // Track vote event
      if (newVoteType) {
        trackVote(newVoteType, alternativeId);
      }

      // Refetch data to update vote counts
      const { data: wordsData } = await supabase
        .from('foreign_words')
        .select(
          `
          *,
          category:categories(*),
          submitter:profiles!foreign_words_submitted_by_fkey(*),
          alternatives(
            *,
            proposer:profiles!alternatives_proposed_by_fkey(*)
          )
        `,
        )
        .order(sortBy === 'popularity' ? 'total_votes' : 'submitted_at', {
          ascending: false,
        });

      if (wordsData) {
        setForeignWords(wordsData);
      }
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('حدث خطأ أثناء التصويت');
    }
  };



  const handleProposeAlternative = (wordId: string) => {
    navigate(`/word/${wordId}/alternatives`);
    toast('سيتم توجيهك لصفحة اقتراح البدائل');
  };

  const handleShare = (wordId: string) => {
    const word = filteredAndSortedWords.find((w) => w.id === wordId);
    if (!word) return;

    const url = `${window.location.origin}/word/${wordId}/alternatives`;

    if (navigator.share) {
      navigator
        .share({
          title: `${word.wordArabic}`,
          text: '',
          url: url,
        })
        .catch((err) => console.log('Error sharing:', err));
    } else {
      navigator.clipboard.writeText(url);
      toast.success('تم نسخ الرابط!');
    }
  };

  const mappedCategories = categories.map((cat) => ({
    id: cat.id,
    name: cat.name,
    nameEnglish: cat.name_english,
  }));

  const users = useExtractUsers(foreignWords);

  const currentUser = user
    ? {
        id: user.id,
        name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
        avatarUrl: user.user_metadata?.avatar_url || null,
      }
    : undefined;

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-slate-900'>
        <div className='text-slate-400'>جاري التحميل...</div>
      </div>
    );
  }

  return (
    <WordDiscovery
      foreignWords={filteredAndSortedWords as ForeignWord[]}
      categories={mappedCategories}
      users={users}
      currentUser={currentUser}
      userVotes={userVotes}
      selectedCategoryId={selectedCategoryId}
      sortBy={sortBy}
      onFilterCategory={handleFilterCategory}
      onSort={handleSort}
      onVote={handleVote}
      onProposeAlternative={handleProposeAlternative}
      onShare={handleShare}
    />
  );
}
